import type { WriteApprovalEntityKind } from "@/components/assistant/formatWriteApprovalSummary";

export type EditablePlanFieldDef =
  | { key: string; label: string; type: "text" | "textarea" | "markdown" | "code" | "readonly" };

export type WriteApprovalPlanDraft = Record<string, string>;

function str(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function getEditablePlanFieldDefs(kind: WriteApprovalEntityKind): EditablePlanFieldDef[] {
  switch (kind) {
    case "custom-role":
    case "draft-legal-role":
      return [
        { key: "name", label: "Name", type: "text" },
        { key: "role_key", label: "Role key", type: "text" },
        { key: "instructions", label: "Instructions", type: "markdown" },
      ];
    case "draft-template":
      return [
        { key: "name", label: "Name", type: "text" },
        { key: "value", label: "Template code", type: "text" },
        { key: "version", label: "Version", type: "text" },
        { key: "blurb", label: "Blurb", type: "textarea" },
        { key: "structureJson", label: "Structure (JSON)", type: "code" },
      ];
    case "draft-clause-library":
      return [
        { key: "section_key", label: "Section", type: "text" },
        { key: "clause_category", label: "Category", type: "text" },
        { key: "posture", label: "Posture", type: "text" },
        { key: "jurisdiction_applicability", label: "Jurisdiction", type: "text" },
        { key: "version", label: "Version", type: "text" },
        { key: "language", label: "Clause language", type: "markdown" },
      ];
    default:
      return [];
  }
}

export function supportsEditableWriteApprovalPlan(kind: WriteApprovalEntityKind): boolean {
  return getEditablePlanFieldDefs(kind).length > 0;
}

function structureToJsonString(structure: unknown): string {
  if (structure == null) return "";
  if (typeof structure === "string") return structure.trim();
  try {
    return JSON.stringify(structure, null, 2);
  } catch {
    return "";
  }
}

function parseStructureJson(text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return undefined;
  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    return trimmed;
  }
}

export function planDraftFromProposal(
  kind: WriteApprovalEntityKind,
  proposal: Record<string, unknown>
): WriteApprovalPlanDraft {
  const id = str(proposal.id);
  const base: WriteApprovalPlanDraft = id ? { id } : {};

  switch (kind) {
    case "custom-role":
    case "draft-legal-role":
      return {
        ...base,
        name: str(proposal.name),
        role_key: str(proposal.role_key),
        instructions: str(proposal.instructions),
      };
    case "draft-template":
      return {
        ...base,
        name: str(proposal.name),
        value: str(proposal.value),
        version: str(proposal.version),
        blurb: str(proposal.blurb),
        structureJson: structureToJsonString(proposal.structure),
      };
    case "draft-clause-library": {
      const templateId = str(proposal.template_id);
      return {
        ...base,
        ...(templateId ? { template_id: templateId } : {}),
        section_key: str(proposal.section_key),
        clause_category: str(proposal.clause_category),
        posture: str(proposal.posture),
        jurisdiction_applicability: str(proposal.jurisdiction_applicability),
        version: str(proposal.version),
        language: str(proposal.language),
      };
    }
    default:
      return base;
  }
}

export function mergePlanDraftForToolInput(
  kind: WriteApprovalEntityKind,
  draft: WriteApprovalPlanDraft
): Record<string, unknown> {
  const id = draft.id?.trim();
  const withId = id ? { id } : {};

  switch (kind) {
    case "custom-role":
    case "draft-legal-role":
      return {
        ...withId,
        name: (draft.name ?? "").trim(),
        role_key: (draft.role_key ?? "").trim(),
        instructions: (draft.instructions ?? "").trim(),
      };
    case "draft-template":
      return {
        ...withId,
        name: (draft.name ?? "").trim(),
        value: (draft.value ?? "").trim(),
        version: (draft.version ?? "").trim(),
        blurb: (draft.blurb ?? "").trim(),
        structure: parseStructureJson(draft.structureJson ?? ""),
      };
    case "draft-clause-library": {
      const templateId = (draft.template_id ?? "").trim();
      return {
        ...withId,
        ...(templateId ? { template_id: templateId } : {}),
        section_key: (draft.section_key ?? "").trim(),
        clause_category: (draft.clause_category ?? "").trim() || null,
        posture: (draft.posture ?? "").trim() || null,
        jurisdiction_applicability: (draft.jurisdiction_applicability ?? "").trim(),
        version: (draft.version ?? "").trim(),
        language: (draft.language ?? "").trim(),
      };
    }
    default:
      return { ...withId };
  }
}

export function proposalFingerprintForKind(
  kind: WriteApprovalEntityKind,
  proposal: Record<string, unknown>
): string {
  return JSON.stringify(mergePlanDraftForToolInput(kind, planDraftFromProposal(kind, proposal)));
}

export function draftFingerprintForKind(
  kind: WriteApprovalEntityKind,
  draft: WriteApprovalPlanDraft
): string {
  return JSON.stringify(mergePlanDraftForToolInput(kind, draft));
}

/** Minimum fields required before Create with proposalOverride (server direct write). */
export function planDraftReadyForDirectWrite(
  kind: WriteApprovalEntityKind,
  draft: WriteApprovalPlanDraft
): boolean {
  const merged = mergePlanDraftForToolInput(kind, draft);
  switch (kind) {
    case "custom-role":
    case "draft-legal-role":
      return Boolean(merged.name && merged.role_key && merged.instructions);
    case "draft-template":
      return Boolean(merged.name && merged.value);
    case "draft-clause-library":
      return Boolean(merged.template_id && merged.section_key && merged.language);
    default:
      return false;
  }
}
